import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './BookSession.css';

// Ensure this points to your backend server. No trailing slash.
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// --- Step 1 Component: UserDetailsForm ---
// This component is correct and requires no changes.
const UserDetailsForm = ({ nextStep, handleChange, values }) => {
    const continueStep = e => {
        e.preventDefault();
        const { studentName, studentEmail, phone, subject, countries } = values;
        if (!studentName || !studentEmail || !phone || !subject || !countries) {
            alert('Please fill out all required fields.');
            return;
        }
        nextStep();
    };

    return (
        <div className="form-container">
            <div className="user-details-panel">
                <h2 className="form-title">Book a Counselling Session</h2>
                <p className="form-subtitle">Step 1: Your Details</p>
                <form onSubmit={continueStep}>
                    <div className="input-group"><input type="text" placeholder="Name" onChange={handleChange('studentName')} defaultValue={values.studentName} required /></div>
                    <div className="input-group"><input type="email" placeholder="Email" onChange={handleChange('studentEmail')} defaultValue={values.studentEmail} required /></div>
                    <div className="input-group"><input type="tel" placeholder="Phone" onChange={handleChange('phone')} defaultValue={values.phone} required /></div>
                    <div className="input-group"><input type="text" placeholder="Subject (e.g., Application Review)" onChange={handleChange('subject')} defaultValue={values.subject} required /></div>
                    <div className="input-group"><input type="text" placeholder="Target Countries (comma-separated)" onChange={handleChange('countries')} defaultValue={values.countries} required /></div>
                    <div className="input-group">
                        <select onChange={handleChange('mode')} defaultValue={values.mode} required>
                            <option value="Video">Video Call</option>
                            <option value="Phone">Phone Call</option>
                        </select>
                    </div>
                    <button type="submit" className="confirm-button">Next: Select Date & Time</button>
                </form>
            </div>
        </div>
    );
};


// --- Step 2 Component: ScheduleSelection ---
// This component is correct and requires no changes.
const ScheduleSelection = ({ prevStep, nextStep, formData, setFormData }) => {
    const availableTimes = ['10:30 am', '10:40 am', '10:50 am', '11:00 am', '11:10 am', '11:20 am', '11:30 am', '11:40 am', '11:50 am', '12:00 pm', '12:10 pm', '12:20 pm', '12:30 pm', '12:40 pm', '12:50 pm', '01:00 pm'];
    const handleDateChange = (e) => setFormData({ ...formData, date: e.target.value, time: '' });
    const handleTimeChange = (time) => setFormData({ ...formData, time });
    const back = e => { e.preventDefault(); prevStep(); };
    const continueToPayment = (e) => {
        e.preventDefault();
        if (!formData.date || !formData.time) {
            alert('Please select both a date and a time slot.');
            return;
        }
        nextStep();
    };

    useEffect(() => {
        if (!formData.date) {
            const today = new Date();
            today.setMinutes(today.getMinutes() - today.getTimezoneOffset());
            const todayISO = today.toISOString().split('T')[0];
            setFormData(prevData => ({ ...prevData, date: todayISO }));
        }
    }, [formData.date, setFormData]);

    const formattedDate = formData.date
        ? new Date(formData.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
        : 'Select a date';

    return (
        <div className="appointment-container">
            <div className="how-it-works-panel">
                <h2>How It Works</h2>
                <div className="step-info"><h4>Step 1: Your Details</h4><p className="completed-text">✓ Completed</p></div>
                <div className="step-info"><h4>Step 2: Schedule</h4><p>Select your preferred date and time.</p></div>
                <button onClick={back} className="dashboard-button">Back to Details</button>
            </div>
            <div className="schedule-panel">
                <form onSubmit={continueToPayment}>
                    <div className="schedule-header"><h2>Schedule Appointment</h2></div>
                    <div className="form-group">
                        <label htmlFor="date">Date</label>
                        <input type="date" id="date" value={formData.date || ''} onChange={handleDateChange} required min={new Date().toISOString().split('T')[0]} />
                    </div>
                    <div className="form-group">
                        <label>Available Times for {formattedDate}</label>
                        <div className="time-slots-grid">
                            {availableTimes.map((time) => (
                                <button type="button" key={time} className={`time-slot-btn ${formData.time === time ? 'selected' : ''}`} onClick={() => handleTimeChange(time)}>
                                    {time}
                                </button>
                            ))}
                        </div>
                    </div>
                    <button type="submit" className="confirm-button" disabled={!formData.time}>
                        Next: Confirm & Pay
                    </button>
                </form>
            </div>
        </div>
    );
};


// --- Step 3 Component: PaymentConfirmation ---
// This component is defined internally and has the CORRECT API endpoint.
const PaymentConfirmation = ({ prevStep, handleSubmit, formData, isLoading }) => {
    const [couponCode, setCouponCode] = useState('');
    const [discount, setDiscount] = useState(0);
    const [couponMessage, setCouponMessage] = useState('');
    const [error, setError] = useState('');

    const BASE_PRICE = 500;
    const finalPrice = Math.max(0, BASE_PRICE - discount);

    const applyCoupon = async () => {
        if (!couponCode) {
            setError('Please enter a coupon code.');
            return;
        }
        setError('');
        setCouponMessage('');
        try {
            // ✅ CORRECTED: This is the correct API endpoint for coupon validation.
            const res = await axios.post(`${API_URL}/api/appointments/coupon/validate`, {
                code: couponCode,
                amount: BASE_PRICE,
            });
            setDiscount(res.data.discount);
            setCouponMessage(res.data.message);
        } catch (err) {
            setDiscount(0);
            setError(err.response?.data?.message || 'Invalid coupon or API error.');
        }
    };

    const proceedToPay = () => {
        handleSubmit({
            ...formData,
            originalAmount: BASE_PRICE,
            finalAmount: finalPrice,
            couponCode: discount > 0 ? couponCode.toUpperCase() : null,
        });
    };

    return (
        <div className="form-container">
            <div className="payment-panel">
                <h2 className="form-title">Step 3: Confirm & Pay</h2>
                <div className="summary">
                    <h4>Booking Summary</h4>
                    <p><strong>Name:</strong> {formData.studentName}</p>
                    <p><strong>Date:</strong> {new Date(formData.date).toLocaleDateString('en-GB')}</p>
                    <p><strong>Time:</strong> {formData.time}</p>
                </div>
                <hr />
                <div className="pricing">
                    <p>Session Price: <span>₹{BASE_PRICE.toFixed(2)}</span></p>
                    {discount > 0 && <p className="discount">Discount: <span>- ₹{discount.toFixed(2)}</span></p>}
                    <h4>Total Amount: <span>₹{finalPrice.toFixed(2)}</span></h4>
                </div>
                <div className="coupon-section">
                    <div className="input-group">
                        <input type="text" placeholder="Enter Coupon Code" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} />
                        <button type="button" onClick={applyCoupon} className="apply-coupon-btn">Apply</button>
                    </div>
                    {error && <p className="error-text">{error}</p>}
                    {couponMessage && <p className="success-text">{couponMessage}</p>}
                </div>
                <div className="button-group">
                    <button onClick={prevStep} className="dashboard-button" disabled={isLoading}>Back</button>
                    <button onClick={proceedToPay} className="confirm-button" disabled={isLoading}>
                        {isLoading ? 'Processing...' : (finalPrice > 0 ? `Pay ₹${finalPrice.toFixed(2)} & Book` : 'Book Free Session')}
                    </button>
                </div>
            </div>
        </div>
    );
};


// --- Main Parent Component: BookSession ---
// This is the main export and orchestrates all the steps.
const BookSession = () => {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        studentName: '', studentEmail: '', phone: '', mode: 'Video', subject: '',
        countries: '', date: '', time: '', duration: '30',
    });
    const [isLoading, setIsLoading] = useState(false);

    const nextStep = () => setStep(step + 1);
    const prevStep = () => setStep(step - 1);
    const handleChange = (input) => (e) => setFormData({ ...formData, [input]: e.target.value });

    const resetForm = () => {
        setStep(1);
        setFormData({
            studentName: '', studentEmail: '', phone: '', mode: 'Video', subject: '',
            countries: '', date: '', time: '', duration: '30'
        });
    };

    const handleSubmit = async (finalFormData) => {
        setIsLoading(true);
        const countriesArray = (finalFormData.countries || '').split(',').map(c => c.trim()).filter(c => c);
        const sessionPayload = { ...finalFormData, countries: countriesArray };

        if (typeof window.Razorpay !== 'function') {
            alert("Payment gateway failed to load. Please check your ad-blocker and refresh.");
            setIsLoading(false);
            return;
        }

        try {
            // This API call now has better error handling on the backend
            const { data } = await axios.post(`${API_URL}/api/appointments/initiate`, {
                amount: finalFormData.finalAmount,
                sessionDetails: sessionPayload
            });

            if (data.isFree) {
                alert(data.message || 'Free session booked successfully!');
                resetForm();
                setIsLoading(false);
                return;
            }

            const { order, key } = data;
            const options = {
                key: key,
                amount: order.amount,
                currency: order.currency,
                name: "Next-Viza-Immigration",
                description: "Counselling Session Booking",
                order_id: order.id,
                handler: async function (response) {
                    setIsLoading(true); // Show loader during verification
                    try {
                        const verificationResult = await axios.post(`${API_URL}/api/appointments/verify`, {
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_signature: response.razorpay_signature,
                        });
                        alert(verificationResult.data.message);
                        if (verificationResult.data.success) {
                            resetForm();
                        }
                    } catch (verifyError) {
                        alert(verifyError.response?.data?.message || 'Payment verification failed. Please contact support.');
                    } finally {
                        setIsLoading(false);
                    }
                },
                prefill: {
                    name: finalFormData.studentName,
                    email: finalFormData.studentEmail,
                    contact: finalFormData.phone,
                },
                theme: { color: "#3399cc" },
            };

            const rzp = new window.Razorpay(options);
            rzp.on('payment.failed', function (response){
                alert(`Payment Failed: ${response.error.description}. Your booking was not completed.`);
                setIsLoading(false);
            });
            rzp.open();
            // --- FIX: Hide the loader when the Razorpay popup opens ---
            // The user is now interacting with the popup, so our app shouldn't look like it's loading.
            setIsLoading(false);

        } catch (error) {
            // --- FIX: This will now display the detailed error from our backend ---
            // e.g., "Payment amount must be at least ₹1.00"
            const errorMessage = error.response?.data?.message || 'An unexpected error occurred. Please try again.';
            alert(errorMessage);
            setIsLoading(false);
        }
    };
    switch (step) {
        case 1:
            return <UserDetailsForm nextStep={nextStep} handleChange={handleChange} values={formData} />;
        case 2:
            return <ScheduleSelection prevStep={prevStep} nextStep={nextStep} formData={formData} setFormData={setFormData} />;
        case 3:
            return <PaymentConfirmation prevStep={prevStep} handleSubmit={handleSubmit} formData={formData} isLoading={isLoading} />;
        default:
            return <div>Loading...</div>;
    }
};

export default BookSession;